import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { ShieldCheck, RefreshCw, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPROVAL_TYPES = [
  {
    key: 'can_approve_po',
    label: 'Approve Purchase Orders',
    description: 'Can approve pending PO/PR requests',
  },
  {
    key: 'can_approve_parts_release',
    label: 'Approve Parts Release',
    description: 'Can approve parts release from store',
  },
  {
    key: 'can_record_grn',
    label: 'Record GRN (Inward)',
    description: 'Can record goods receipt against a PO',
  },
  {
    key: 'can_approve_indent',
    label: 'Approve Indent',
    description: 'Can approve indent raised to HO/local vendor',
  },
];

export default function ApprovalRightsPage({ user }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(''); // role_id being saved

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/roles`, { headers: authHeaders() });
      setRoles(res.data);
    } catch {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleToggle = async (roleId, rightKey, currentValue) => {
    setSaving(roleId + rightKey);
    try {
      await axios.patch(
        `${API}/roles/${roleId}/approval-rights`,
        { [rightKey]: !currentValue },
        { headers: authHeaders() }
      );
      setRoles(prev => prev.map(r => {
        if (r.id !== roleId) return r;
        return {
          ...r,
          approval_rights: {
            ...(r.approval_rights || {}),
            [rightKey]: !currentValue,
          }
        };
      }));
      toast.success('Approval rights updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving('');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-indigo-600" />
            Approval Rights
          </h1>
          <p className="text-slate-500 mt-1">
            Control which roles can approve purchase orders, parts releases, and goods receipts.
          </p>
        </div>
        <button
          onClick={fetchRoles}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
          data-testid="refresh-approval-rights"
        >
          <RefreshCw className="h-4 w-4" />Refresh
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 text-sm text-amber-800">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          Admins and Super Admins always have all approval rights regardless of this table.
          Use these toggles to grant specific approval abilities to other roles.
        </span>
      </div>

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-52 font-semibold">Role</TableHead>
                {APPROVAL_TYPES.map(t => (
                  <TableHead key={t.key} className="text-center min-w-[160px]">
                    <div className="flex flex-col items-center gap-1">
                      <span className="font-semibold text-xs leading-tight">{t.label}</span>
                      <span className="text-slate-400 text-[10px] font-normal leading-tight">{t.description}</span>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={APPROVAL_TYPES.length + 1} className="text-center text-slate-400 py-8">
                    No roles found. Create roles in Role Management first.
                  </TableCell>
                </TableRow>
              )}
              {roles.map(role => (
                <TableRow key={role.id} className="hover:bg-slate-50/60">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: role.color || '#6366f1' }}
                      />
                      <div>
                        <p className="font-semibold text-sm text-slate-900">{role.name}</p>
                        {role.department && <p className="text-xs text-slate-400">{role.department}</p>}
                      </div>
                      {role.user_count > 0 && (
                        <Badge variant="secondary" className="text-xs ml-1">
                          {role.user_count} user{role.user_count !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  {APPROVAL_TYPES.map(t => {
                    const currentVal = role.approval_rights?.[t.key] ?? false;
                    const isSaving = saving === role.id + t.key;
                    return (
                      <TableCell key={t.key} className="text-center">
                        <div className="flex justify-center">
                          <Switch
                            checked={currentVal}
                            onCheckedChange={() => handleToggle(role.id, t.key, currentVal)}
                            disabled={isSaving}
                            data-testid={`toggle-${role.id}-${t.key}`}
                          />
                        </div>
                        {currentVal && (
                          <p className="text-[10px] text-green-600 font-medium mt-0.5">Enabled</p>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
